import axios, { AxiosResponse, RawAxiosRequestConfig } from "axios";
import { RemoveEventHandle } from "csi/typings";
import Cookie from "js-cookie";

const ERROR_UNHANDLED_EXCEPTION = "UNHANDLED_EXCEPTION";

export const REQUEST_HANDLER = "/csi-requesthandler/plm";
export const REQUEST_MODULE = "Module";
export const REQUEST_OPERATION = "Operation";

export const PARAM_FMT_VERSION = "Fmt.Version";
export const PARAM_FMT_VERSION_VALUE = "2";

export const PARAM_OUTPUT_JSON = "OutputJSON";
export const PARAM_OUTPUT_JSON_VALUE = "2";

export const MODULE_PUBLISHER = "Publisher";
export const MODULE_DOCUMENT_PROCESSING = "DocumentProcessing";

export type JsonParseReviverFunction = (key: string, value: any) => any;

/** interface for failed requests */
interface IErrorResponse {
	Error: string;
	ErrorAdmin?: string;
	Exception?: string;
	Param?: Array<number | string>;
	Status: string;
}

export class C8RequestError extends Error {
	constructor(
		message: string,
		readonly csiErrorCode: string,
		readonly csiErrorParams: Array<number | string>,
		readonly csiErrorException: string,
	) {
		super(message);
	}
}

export type AxiosResponseCallbackFunction = (response: AxiosResponse, formData: FormData, startTime?: number, endTime?: number) => void;

/** functions to call after any response is received */
const responseCallbacks = new Set<AxiosResponseCallbackFunction>();

/** register function to call when response is received */
export function registerResponseCallback(callback: AxiosResponseCallbackFunction): RemoveEventHandle {
	responseCallbacks.add(callback);

	return {
		remove(): void {
			responseCallbacks.delete(callback);
		},
	};
}

/** create FormData from plain object */
export function createFormData(obj: Record<string, any>, useJson = true): FormData {
	const fd = new FormData();
	const add = (key: string, value: any): void => {
		if (value != null) {
			fd.append(key, value);
		}
	};

	Object.entries(obj)
		.forEach(([key, value]) => {
			if (value instanceof Array) {
				value.forEach((v) => add(key, v));
			} else {
				add(key, value);
			}
		});

	if (useJson) {
		fd.append(PARAM_FMT_VERSION, PARAM_FMT_VERSION_VALUE);
		fd.append(PARAM_OUTPUT_JSON, PARAM_OUTPUT_JSON_VALUE);
	}

	return fd;
}

/** return true if given error code is an unhandled exception */
export function errorIsUnhandledException(errorCode: string): boolean {
	return errorCode?.indexOf(ERROR_UNHANDLED_EXCEPTION) >= 0;
}

/** return default config to use in multi-part form data request */
export function getFormDataConfig(reviver?: JsonParseReviverFunction, signal?: AbortSignal): RawAxiosRequestConfig<Record<string, any>> {
	// TODO: Keycloak bearer token - _addBearerToken in csi/comm/Request?
	// do we need to bring Keycloak into c8-platform
	const config: RawAxiosRequestConfig<Record<string, any>> = {
		headers: {
			"Content-Type": "multipart/form-data",
			"X-CSRF-Token": Cookie.get("CSRFToken"),
		},
	};

	if (reviver) {
		config.responseType = "text";
		config.transformResponse = (data: string): object => JSON.parse(data, reviver);
	}

	if (signal) {
		config.signal = signal;
	}

	return config;
}

/** return request url to access a file alternate file */
export function getAlternateFileRequestUrl(fileUrl: string, filename:string, encodeParameterValues = true): string {
	return getRequestUrl([
		[REQUEST_MODULE, MODULE_DOCUMENT_PROCESSING],
		[REQUEST_OPERATION, "GetAlternateFile"],
		["URL", fileUrl],
		["Filename", filename],
	], encodeParameterValues);
}

/** return request url to access a file in the file vault */
export function getFileVaultRequestUrl(fileUrl: string, encodeParameterValues = true): string {
	return getRequestUrl([
		[REQUEST_MODULE, MODULE_PUBLISHER],
		[REQUEST_OPERATION, "GetDirect"],
		["URL", fileUrl],
	], encodeParameterValues);
}

/** return request url for given params */
export function getRequestUrl(params: Array<[string, string]>, encodeParameterValues = true): string {
	const tokens = params.map(([param, value]) => `${param}=${encodeParameterValues ? encodeURIComponent(value) : value}`);
	return `${REQUEST_HANDLER}?${tokens.join("&")}`;
}

/** post request to the server and return data using the standard request handler by default */
export async function postFormData<T>(formData: FormData, config = getFormDataConfig(), url = REQUEST_HANDLER): Promise<T> {
	const startTime = Date.now();
	
	const response: AxiosResponse<T | IErrorResponse> = await axios.post<T>(url, formData, config);

	const endTime = Date.now();
	for (const callback of responseCallbacks) {
		callback(response, formData, startTime, endTime);
	}

	const { data } = response;
	const errorResponse = data as IErrorResponse;
	const { Error, ErrorAdmin, Exception, Param } = errorResponse;

	if (Error) {
		throw new C8RequestError(ErrorAdmin, Error, Param, Exception);
	}

	return data as T;
}
